import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import ListPicker from '@components/picker/ListPicker'
import TextInput from '@components/textInput/TextInput'
import Button from '@components/button/Button'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'
import useDebounce from '@utils/useDebounce'

import sanitize from '@utils/Sanitizer'

import { ControleQualiteTypeControle } from '@api/enum'

interface Props {
  selected?: ControleQualiteTypeControle
  onTypeSelected: (item?: ControleQualiteTypeControle) => void
  style?: 'default' | 'list'
}

const ControleTypeFilter = ({ selected, onTypeSelected, style = 'default' }: Props) => {
  const i18n = useI18n()
  const [, bp] = useBreakpoint()

  const [local, setLocal] = React.useState(selected)
  const [search, debounced, setSearch] = useDebounce('')

  const data = React.useMemo(
    () =>
      [ControleQualiteTypeControle.VIPARIS, ControleQualiteTypeControle.AUTO]
        .filter((p) => sanitize(p).includes(sanitize(debounced)))
        .map((p) => ({ label: i18n.t(`filters.controle.type.${p}`), value: p })),
    [debounced]
  )

  React.useEffect(() => {
    setLocal(selected)
  }, [selected])

  const onItemSelected = (item: ControleQualiteTypeControle) => {
    const isSelected = local === item
    const next = isSelected ? undefined : item

    if (style === 'list') {
      onTypeSelected(next)
    } else {
      setLocal(next)
    }
  }

  return (
    <>
      <SearchContainer>
        <TextInput
          onChange={setSearch}
          searchStyle={bp === 'phone' ? 'onLight' : 'onDark'}
          left={{ name: 'search' }}
          placeholder={i18n.t('common.search')}
          value={search}
        />
      </SearchContainer>
      <FilterView>
        <ListPicker data={data} onItemSelected={onItemSelected} selected={local ? [local] : []} />
      </FilterView>

      {style === 'default' && (
        <Footer>
          {!!local ? (
            <TouchableOpacity activeOpacity={0.9} onPress={() => setLocal(undefined)}>
              <Text>{i18n.t('common.erase')}</Text>
            </TouchableOpacity>
          ) : (
            <View />
          )}
          <Button label={i18n.t('common.save')} onClick={() => onTypeSelected(local)} status="active" />
        </Footer>
      )}
    </>
  )
}

export default ControleTypeFilter

const SearchContainer = styled(View)`
  padding: 16px 16px 0;
`

const FilterView = styled(View)`
  flex: 1;
  padding: 31px 22px 31px 31px;
`

const Footer = styled(View)`
  border-top-width: 1px;
  border-top-color: ${(props) => props.theme.colors.abandoned};
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 16px 31px;
`
